/**
 * @module Views/AdminDashboard/GstReportsPage
 * @description React component for viewing and downloading GST reports.
 */

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fetchReportBlob, downloadBlob } from 'services/reportService';
import axios from 'axios';
import { ContainedButton1 } from 'components/DesignSystem/Button';
import CustomDatePicker from 'components/DesignSystem/CustomDatePicker';
import useSwalAlert from 'components/DesignSystem/SwalAlert';
import { CustomTableCell, CustomTablePagination, CustomTableRow } from 'components/DesignSystem/Table/CustomTable';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { filter } from 'lodash';
import Scrollbar from 'oldLayouts/components/scrollbar';
import { UserListHead } from 'oldLayouts/components/user';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import PageContainer from 'views/dashboard/PageContainer';
import * as Yup from 'yup';

const GST_TABLE_HEAD = [
  // { id: "edit-action" },
  { id: 'fromDate', label: 'From Date', alignRight: false },
  { id: 'toDate', label: 'To Date', alignRight: false },
  { id: 'totalCgst', label: 'CGST', alignRight: false },
  { id: 'totalSgst', label: 'SGST', alignRight: false },
  { id: 'totalIgst', label: 'IGST', alignRight: false },
  { id: 'totalGst', label: 'Total GST', alignRight: false },
  { id: 'action', label: 'Action', alignCenter: true },
];

/**
 * @component
 * React component for GST reports page.
 * @returns {JSX.Element} The rendered component.
 */
export default function GstReportsPage() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [gstReports, setGstReports] = useState([]);
  const [commissionReports, setCommissionReports] = useState([]);
  const [reportFetch, setReportFetch] = useState(false);
  // Tracks which download button is currently in flight so we can disable it
  // and show a spinner until the report finishes downloading. null = idle.
  const [downloadingKey, setDownloadingKey] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const showSwalAlert = useSwalAlert();

  /**
   * Handles sorting requests.
   * @param {Event} event - Event object.
   * @param {string} property - Sorting key.
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = gstReports.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  /**
   * Handles page change.
   * @param {Event} event - Event object.
   * @param {number} newPage - New page index.
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handles rows per page change.
   * @param {Event} event - Event object.
   */
  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - commissionReports.length) : 0;

   /**
   * Formik instance for managing form state and validation.
   */
  const formik = useFormik({
    initialValues: {
      reportType: 'gst',
      fromDate: dayjs().subtract(1, 'month').startOf('month'),
      toDate: dayjs().subtract(1, 'month').endOf('month'),
    },
    validationSchema: Yup.object().shape({
      reportType: Yup.string().required('Select the Report Type to view'),
      fromDate: Yup.date()
        .required('From Date is required')
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('01-01-1900'), 'Date must be greater than 01-01-1900')
        .max(dayjs().endOf('day'), 'Date must be equal or before today'),
      toDate: Yup.date()
        .required('To Date is required')
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('01-01-1900'), 'Date must be greater than 01-01-1900')
        .max(dayjs().endOf('day'), 'Date must be equal or before today')
        .test({
          name: 'notBeforeAsFromDate',
          message: 'To Date must be equal or greater than from date',
          test: (toDate, values) => {
            const fromDate = values.parent.fromDate;
            return toDate !== null ? dayjs(fromDate).isSameOrBefore(dayjs(toDate), 'day') : false;
          },
        }),
    }),
    onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
      setReportFetch(false);
      axios
        .post('/reports', values)
        .then((response) => {
          if (response.status === 200) {
            setReportFetch(true);
            if (values.reportType === 'gst') setGstReports(response.data.data);
          }
          const icon = 'success';
          showSwalAlert({
            icon: `${icon}`,
            title: `${response.data.message}`,
            timer: 3000,
          });
        })
        .catch((err) => {
          console.error(err);
          showSwalAlert({
            icon: 'error',
            title: 'Failed to fetch report',
            timer: 3000,
          });
        });
    },
  });

  const handleDateChange = (value, name) => {
    const event = {
      target: {
        name,
        value: value !== null ? dayjs(value) : null,
      },
    };
    formik.handleChange(event);
  };

  const generateGstReport = async (fromDate, toDate, downloadKey) => {
    // Guard against duplicate clicks: ignore if a download is already running.
    if (downloadingKey) return;
    setDownloadingKey(downloadKey);
    try {
      const response = await axios.post('/reports/generate-gst-report', { fromDate, toDate });
      if (response.status === 200) {
        const fileKey = response.data.fileKey;
        // Fetch the generated report from the backend, which streams it
        // from S3 server-side (no AWS secret exposed to the browser).
        const blob = await fetchReportBlob(fileKey);
        downloadBlob(
          blob,
          `GWD-Detail-GST-Report-${dayjs(fromDate).format('DD-MM-YYYY')} to ${dayjs(toDate).format('DD-MM-YYYY')}.pdf`
        );
      }
    } catch (err) {
      console.error('Error downloading file:', err);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleBackClick = () => {
    navigate('/admin/analyticaldashboard');
  };

  return (
    <>
      <PageContainer showBackButton showHelpIcon={false}>
        <Helmet>
          <title> GST Reports | Get Will Done </title>
        </Helmet>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="title1" gutterBottom>
            GST Reports
          </Typography>
          {/* <div style={{ flexGrow: 1 }}>
            <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />
          </div> */}
        </Stack>
        <form noValidate onSubmit={formik.handleSubmit}>
          <Grid container spacing={1} sx={{ marginBottom: 1 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography width={'30%'} variant="bodyText4">
                  From Date
                </Typography>
                <FormControl required error={formik.errors.fromDate} fullWidth variant="standard">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CustomDatePicker
                      value={formik.values.fromDate}
                      name="fromDate"
                      error={formik.errors.fromDate}
                      fullWidth
                      inputFormat={'dd-MM-yyyy'}
                      maxDate={dayjs()}
                      maxDateMessage="Past dates are not allowed"
                      onChange={(value) => handleDateChange(value, 'fromDate')}
                      onBlur={formik.handleBlur}
                      placeholder="From Date(DD-MM-YYYY)"
                    />
                    {true ||
                      (formik.touched.fromDate && formik.errors.fromDate && (
                        <FormHelperText error id={`standard-weight-helper-text-fromDate`}>
                          <Typography variant="suggestiveText">{formik.errors.fromDate || 'error'}</Typography>
                        </FormHelperText>
                      ))}
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography width={'25%'} variant="bodyText4">To Date</Typography>
                <FormControl required error={formik.errors.toDate} fullWidth variant="standard">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CustomDatePicker
                      value={formik.values.toDate}
                      name="toDate"
                      error={formik.errors.toDate}
                      fullWidth
                      inputFormat={'dd-MM-yyyy'}
                      maxDate={dayjs()}
                      onChange={(value) => handleDateChange(value, 'toDate')}
                      onBlur={formik.handleBlur}
                      placeholder="To Date(DD-MM-YYYY)"
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                      <FormHelperText error id={`standard-weight-helper-text-toDate`}>
                        <Typography variant="suggestiveText">{formik.errors.toDate}</Typography>
                      </FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Tooltip title={`Apply`}>
                <ContainedButton1 id={'new-generic'} type="submit" sx={{ height: '40px' }}>
                  <Typography variant="bodyText3">Apply</Typography>
                </ContainedButton1>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              {formik.errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>{formik.errors.submit}</FormHelperText>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
        <Box>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {formik.values.reportType === 'gst' && (
                <>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={GST_TABLE_HEAD}
                      rowCount={gstReports.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {gstReports.map((row, index) => {
                        const { fromDate, toDate, totalCgst, totalSgst, totalIgst } = row;
                        const totalGst = +totalCgst + +totalSgst + +totalIgst;
                        return (
                          <>
                            <CustomTableRow sx={{ bgcolor: (theme) => 'initial' }} key={index + 1} tabIndex={-1}>
                              <CustomTableCell>{dayjs(fromDate).format('DD-MM-YYYY')}</CustomTableCell>
                              <CustomTableCell>{dayjs(toDate).format('DD-MM-YYYY')}</CustomTableCell>
                              <CustomTableCell>₹{totalCgst ?? 0}</CustomTableCell>
                              <CustomTableCell>₹{totalSgst ?? 0}</CustomTableCell>
                              <CustomTableCell>₹{totalIgst ?? 0}</CustomTableCell>
                              <CustomTableCell>₹{totalGst.toFixed(2)}</CustomTableCell>
                              <CustomTableCell>
                                {(totalCgst > 0 || totalSgst > 0 || totalIgst > 0 || totalGst > 0) && (
                                  <Tooltip title="Download Detail Report">
                                    <span>
                                      <Button
                                        fullWidth
                                        sx={{ paddingTop: '7px', paddingBottom: '7px' }}
                                        disabled={downloadingKey !== null}
                                        onClick={() => generateGstReport(fromDate, toDate, `gst-${index}`)}
                                        startIcon={downloadingKey === `gst-${index}` ? <CircularProgress size={16} /> : null}
                                      >
                                        {downloadingKey === `gst-${index}` ? (
                                          'Downloading...'
                                        ) : (
                                          <Chip
                                            sx={{ cursor: 'pointer' }}
                                            label={`Download Detail Report`}
                                            variant="outlined"
                                          />
                                        )}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </CustomTableCell>
                            </CustomTableRow>
                          </>
                        );
                      })}
                      {emptyRows > 0 && (
                        <CustomTableRow style={{ height: 53 * emptyRows }}>
                          <CustomTableCell colSpan={6} />
                        </CustomTableRow>
                      )}
                    </TableBody>

                    {reportFetch && gstReports.length === 0 && (
                      <TableBody>
                        <CustomTableRow>
                          <CustomTableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            No GST Found
                          </CustomTableCell>
                        </CustomTableRow>
                      </TableBody>
                    )}
                  </Table>
                  <CustomTablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={gstReports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </TableContainer>
          </Scrollbar>
        </Box>
      </PageContainer>
    </>
  );
}
